import type { ChordPayload, DrumPayload, NotePayload, RuntimeAction, TriggerAction } from '../../scheduler/actions';
import type { AudioEnvironment, GainLike, OscillatorLike } from './audioEnvironment';
import {
  chordPayloadToMidiNotes,
  drumSpecForTrigger,
  midiToFrequency,
  notePayloadToMidi,
  timbreForRole,
  velocityToGain,
} from './voices';

const ATTACK_SECONDS = 0.005;
const FORCED_RELEASE_SECONDS = 0.015;
const DEFAULT_MAX_POLYPHONY = 24;

interface OscVoice {
  readonly oscillators: readonly OscillatorLike[];
  readonly gain: GainLike;
  readonly trackId: string;
  stopped: boolean;
}

export interface WebAudioPreviewPlayerOptions {
  readonly audio: AudioEnvironment;
  readonly maxPolyphony?: number;
}

/**
 * Preview-Backend: uebersetzt neutrale RuntimeActions in einfache, hoerbar
 * unterscheidbare Toene. Kein Anspruch auf produktionsreife Synthese - siehe
 * LIMITATIONS.md.
 */
export class WebAudioPreviewPlayer {
  private readonly audio: AudioEnvironment;
  private readonly maxPolyphony: number;
  private readonly masterGain: GainLike;
  private readonly trackGains = new Map<string, GainLike>();
  private trackLevels = new Map<string, number>();
  private readonly activeVoices = new Map<string, OscVoice>();

  constructor(options: WebAudioPreviewPlayerOptions) {
    this.audio = options.audio;
    this.maxPolyphony = options.maxPolyphony ?? DEFAULT_MAX_POLYPHONY;
    this.masterGain = this.audio.createGain();
    this.masterGain.gain.value = 0.8;
    this.masterGain.connect(this.audio.destination);
  }

  setMasterVolume(value: number): void {
    this.masterGain.gain.value = clamp01(value);
  }

  setTrackLevel(trackId: string, level: number): void {
    const clamped = clamp01(level);
    this.trackLevels.set(trackId, clamped);
    const gainNode = this.trackGains.get(trackId);
    if (gainNode) {
      gainNode.gain.value = clamped;
    }
  }

  handleAction(action: RuntimeAction): void {
    switch (action.kind) {
      case 'trigger':
        this.handleTrigger(action);
        break;
      case 'release':
        this.forceRelease(action.voiceId, action.time);
        break;
      case 'panic':
        this.panic();
        break;
      case 'transportStop':
        this.stopAll(action.time);
        break;
      case 'transportStart':
      case 'control':
        break;
    }
  }

  /** Beendet alle Stimmen sofort, unabhaengig von ihrer geplanten Stopzeit. */
  panic(): void {
    const now = this.audio.currentTime;
    for (const voiceId of Array.from(this.activeVoices.keys())) {
      this.forceRelease(voiceId, now, true);
    }
  }

  private stopAll(time: number): void {
    for (const voiceId of Array.from(this.activeVoices.keys())) {
      this.forceRelease(voiceId, time);
    }
  }

  private getTrackGain(trackId: string): GainLike {
    let node = this.trackGains.get(trackId);
    if (!node) {
      node = this.audio.createGain();
      node.gain.value = this.trackLevels.get(trackId) ?? 1;
      node.connect(this.masterGain);
      this.trackGains.set(trackId, node);
    }
    return node;
  }

  private enforcePolyphonyLimit(): void {
    while (this.activeVoices.size >= this.maxPolyphony) {
      const oldestId = this.activeVoices.keys().next().value as string | undefined;
      if (oldestId === undefined) break;
      this.forceRelease(oldestId, this.audio.currentTime, true);
    }
  }

  private handleTrigger(action: TriggerAction): void {
    this.enforcePolyphonyLimit();
    const trackGain = this.getTrackGain(action.trackId);
    const envelope = this.audio.createGain();
    envelope.gain.value = 0;
    envelope.connect(trackGain);

    const oscillators = this.buildOscillators(action);
    const peakGain = velocityGainFor(action);
    const startTime = action.time;
    const attackEnd = startTime + ATTACK_SECONDS;
    const stopTime = action.eventKind === 'drum' ? startTime + drumFallbackDuration(action) : startTime + Math.max(action.gateSeconds, ATTACK_SECONDS * 2);

    envelope.gain.setValueAtTime(0, startTime);
    envelope.gain.linearRampToValueAtTime(peakGain, attackEnd);
    envelope.gain.setValueAtTime(peakGain, Math.max(attackEnd, stopTime - ATTACK_SECONDS));
    envelope.gain.linearRampToValueAtTime(0, stopTime);

    for (const osc of oscillators) {
      osc.connect(envelope);
      osc.start(startTime);
      osc.stop(stopTime + 0.001);
    }

    const voice: OscVoice = { oscillators, gain: envelope, trackId: action.trackId, stopped: false };
    this.activeVoices.set(action.voiceId, voice);

    const primary = oscillators[0];
    if (primary) {
      primary.onended = () => this.cleanupVoice(action.voiceId);
    }
  }

  private buildOscillators(action: TriggerAction): readonly OscillatorLike[] {
    const timbre = timbreForRole(action.role, action.trackId);
    if (action.eventKind === 'chord') {
      const notes = chordPayloadToMidiNotes(action.payload as ChordPayload);
      return notes.map((midi) => {
        const osc = this.audio.createOscillator();
        osc.type = timbre.waveform;
        osc.frequency.value = midiToFrequency(midi);
        return osc;
      });
    }
    if (action.eventKind === 'drum') {
      const payload = action.payload as DrumPayload;
      const spec = drumSpecForTrigger(payload.triggerId);
      const osc = this.audio.createOscillator();
      osc.type = spec.waveform;
      osc.frequency.setValueAtTime(spec.startFrequency, action.time);
      osc.frequency.linearRampToValueAtTime(spec.endFrequency, action.time + spec.durationSeconds);
      return [osc];
    }
    const payload = action.payload as NotePayload;
    const midi = notePayloadToMidi(payload);
    const osc = this.audio.createOscillator();
    osc.type = timbre.waveform;
    osc.frequency.value = midiToFrequency(midi);
    return [osc];
  }

  private forceRelease(voiceId: string, time: number, immediate = false): void {
    const voice = this.activeVoices.get(voiceId);
    if (!voice || voice.stopped) {
      return;
    }
    voice.stopped = true;
    const releaseEnd = immediate ? time + 0.001 : time + FORCED_RELEASE_SECONDS;
    voice.gain.gain.cancelScheduledValues(time);
    voice.gain.gain.setValueAtTime(voice.gain.gain.value, time);
    voice.gain.gain.linearRampToValueAtTime(0, releaseEnd);
    for (const osc of voice.oscillators) {
      try {
        osc.stop(releaseEnd + 0.001);
      } catch {
        // Oszillator war bereits gestoppt - kein Doppel-Stop noetig.
      }
    }
    this.cleanupVoice(voiceId);
  }

  private cleanupVoice(voiceId: string): void {
    const voice = this.activeVoices.get(voiceId);
    if (!voice) return;
    for (const osc of voice.oscillators) {
      osc.disconnect();
    }
    voice.gain.disconnect();
    this.activeVoices.delete(voiceId);
  }

  get activeVoiceCount(): number {
    return this.activeVoices.size;
  }
}

function velocityGainFor(action: TriggerAction): number {
  return velocityToGain(action.payload.velocity);
}

function drumFallbackDuration(action: TriggerAction): number {
  const payload = action.payload as DrumPayload;
  return drumSpecForTrigger(payload.triggerId).durationSeconds;
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}
